import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import countries from 'iso-3166-country-list';

import { ADDRESS_FIELDS } from 'constants/formFields';
import { evAddOrEditAddress } from 'events/account';

import { AriaLiveTee } from 'components/common/AriaLive';
import { ChangeDisclaimer, ShippingDisclaimer } from 'components/account/Disclaimers';
import { makeFormErrorMessage } from 'helpers/MyAccountRenderUtils';
import css from 'styles/components/account/shippingForm.scss';
import marketplace from 'cfg/marketplace.json';
import { titaniteView, track } from 'apis/amethyst';

const { checkout: { embargoedCountries } } = marketplace;

class ShippingForm extends Component {
  state = {
    isPrimary: this.props.shipping.address.isDefaultShippingAddress
  };

  componentDidMount() {
    titaniteView();
  }

  componentWillUnmount() {
    const { onResetShippingForm } = this.props;
    onResetShippingForm();
  }

  handleCancelClick = () => {
    const { props: { returnTo, onCancelClick = null } } = this;
    if (onCancelClick) {
      onCancelClick();
    } else {
      this.context.router.pushPreserveAppRoot(returnTo || '/addresses');
    }
  };

  handleFormSubmit = event => {
    event.preventDefault();
    const { isPrimary } = this.state;
    const { shipping: { address }, onChangeShippingAddress, returnTo } = this.props;

    // Only top-level `isDefaultShippingAddress` field is modified, so shallow copy is sufficient
    // This is done to prevent `isDefaultShippingAddress` from changing immediately and updating
    // the render() function, which toggles "This is your primary address" and
    // "Make this my primary shipping address" input checkbox
    const shallowAddressCopy = Object.assign({}, address, {
      isDefaultShippingAddress: isPrimary
    });

    Promise.resolve(onChangeShippingAddress(shallowAddressCopy, returnTo)).then(response => {
      if (response?.addressId) {
        const { addressId, valid } = response;
        track(evAddOrEditAddress, { valid, addressId, addressType: 2 }); // amethyst addressType 2 => shipping
      }
    });
  };

  handleUpdateMailingAddressOrName = event => {
    const { onUpdateMailingAddressOrName } = this.props;
    const { name, value } = event.currentTarget;
    onUpdateMailingAddressOrName({ name, value });
  };

  handleUpdateShippingPhone = event => {
    const { onUpdateShippingPhone } = this.props;
    const { name, value } = event.currentTarget;
    onUpdateShippingPhone({ name, value });
  };

  handleUpdateShippingDefaultAddress = () => {
    this.setState({ isPrimary: !this.state.isPrimary });
  };

  render() {
    const { isPrimary } = this.state;
    const {
      isReducedInfo = false,
      primaryVerbiage,
      shipping: {
        address,
        isFormValid,
        isLoading,
        formError
      }
    } = this.props;
    const { name, mailingAddress, phone, isDefaultShippingAddress } = address;
    const errors = mailingAddress.errors || {};
    const { testId } = this.context;

    return (
      <div>
        { !isReducedInfo && <ChangeDisclaimer/> }
        {formError && <AriaLiveTee><div className={css.errorMsg}>{makeFormErrorMessage(formError)}</div></AriaLiveTee>}
        <form method="post" onSubmit={this.handleFormSubmit}>
          <div className={cn(css.formField, { [css.fieldError]: errors[ADDRESS_FIELDS.FULL_NAME.fieldName] || formError })}>
            <label htmlFor={ADDRESS_FIELDS.FULL_NAME.fieldName}>Full Name</label>
            <input
              // When this mounts, the customer selected to snail mail a label, so auomatically focus the first input field of that form
              // Autofocus is ok in this instance, as it happens on user interaction
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              name={ADDRESS_FIELDS.FULL_NAME.fieldName}
              autoComplete={ADDRESS_FIELDS.FULL_NAME.autoComplete}
              id={ADDRESS_FIELDS.FULL_NAME.fieldName}
              data-test-id={testId('addressName')}
              value={name.fullName || ''}
              placeholder="First and Last"
              maxLength={ADDRESS_FIELDS.FULL_NAME.maxLength}
              onChange={this.handleUpdateMailingAddressOrName}
              required/>
            <div>{errors[ADDRESS_FIELDS.FULL_NAME.fieldName]}</div>
          </div>
          <div className={cn(css.formField, { [css.fieldError]: errors[ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName] || formError })}>
            <label htmlFor={ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName}>Street Address</label>
            <input
              name={ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName}
              autoComplete={ADDRESS_FIELDS.ADDRESS_LINE_1.autoComplete}
              id={ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName}
              data-test-id={testId('addressLine1')}
              value={mailingAddress.addressLine1 || ''}
              placeholder="1234 Street Name"
              maxLength={ADDRESS_FIELDS.ADDRESS_LINE_1.maxLength}
              onChange={this.handleUpdateMailingAddressOrName}
              required/>
            <div>{errors[ADDRESS_FIELDS.ADDRESS_LINE_1.fieldName]}</div>
          </div>
          <div className={cn(css.formField, { [css.fieldError]: errors[ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName] || formError })}>
            <label className="screenReadersOnly" htmlFor={ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName}>Apartment Or Unit Number</label>
            <input
              name={ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName}
              autoComplete={ADDRESS_FIELDS.ADDRESS_LINE_2.autoComplete}
              id={ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName}
              data-test-id={testId('addressLine2')}
              value={mailingAddress.addressLine2 || ''}
              placeholder="Apt/Unit #"
              maxLength={ADDRESS_FIELDS.ADDRESS_LINE_2.maxLength}
              onChange={this.handleUpdateMailingAddressOrName}/>
            <div>{errors[ADDRESS_FIELDS.ADDRESS_LINE_2.fieldName]}</div>
          </div>
          <div className={css.splitFormField}>
            <div className={cn(css.formField, { [css.fieldError]: errors[ADDRESS_FIELDS.CITY.fieldName] || formError })}>
              <label htmlFor={ADDRESS_FIELDS.CITY.fieldName}>City</label>
              <input
                name={ADDRESS_FIELDS.CITY.fieldName}
                autoComplete={ADDRESS_FIELDS.CITY.autoComplete}
                id={ADDRESS_FIELDS.CITY.fieldName}
                data-test-id={testId('addressCity')}
                value={mailingAddress.city || ''}
                placeholder="Enter City"
                maxLength={ADDRESS_FIELDS.CITY.maxLength}
                onChange={this.handleUpdateMailingAddressOrName}
                required/>
              <div>{errors[ADDRESS_FIELDS.CITY.fieldName]}</div>
            </div>
            <div className={cn(css.formField, { [css.fieldError]: errors[ADDRESS_FIELDS.STATE_OR_REGION.fieldName] || formError })}>
              <label htmlFor={ADDRESS_FIELDS.STATE_OR_REGION.fieldName}>State/Region</label>
              <input
                name={ADDRESS_FIELDS.STATE_OR_REGION.fieldName}
                autoComplete={ADDRESS_FIELDS.STATE_OR_REGION.autoComplete}
                id={ADDRESS_FIELDS.STATE_OR_REGION.fieldName}
                data-test-id={testId('stateOrRegionText')}
                value={mailingAddress.stateOrRegion || ''}
                placeholder="State"
                maxLength={ADDRESS_FIELDS.STATE_OR_REGION.maxLength}
                onChange={this.handleUpdateMailingAddressOrName}
                required/>
              <div>{errors[ADDRESS_FIELDS.STATE_OR_REGION.fieldName]}</div>
            </div>
          </div>
          <div className={cn(css.formField, { [css.fieldError]: errors[ADDRESS_FIELDS.COUNTRY_CODE.fieldName] || formError })}>
            <label htmlFor={ADDRESS_FIELDS.COUNTRY_CODE.fieldName}>Country</label>
            <select
              id={ADDRESS_FIELDS.COUNTRY_CODE.fieldName}
              name={ADDRESS_FIELDS.COUNTRY_CODE.fieldName}
              autoComplete={ADDRESS_FIELDS.COUNTRY_CODE.autoComplete}
              data-test-id={testId('countryCode')}
              value={mailingAddress.countryCode || ''}
              onChange={this.handleUpdateMailingAddressOrName}>
              {countries
                .filter(country => embargoedCountries.indexOf(country.code) < 0)
                .map(country => <option key={country.code} value={country.code}>{country.name}</option>)
              }
            </select>
            <div>{errors[ADDRESS_FIELDS.COUNTRY_CODE.fieldName]}</div>
          </div>
          <div className={css.splitFormField}>
            <div className={cn(css.formField, { [css.fieldError]: errors[ADDRESS_FIELDS.POSTAL_CODE.fieldName] || formError })}>
              <label htmlFor={ADDRESS_FIELDS.POSTAL_CODE.fieldName}>Zip</label>
              <input
                name={ADDRESS_FIELDS.POSTAL_CODE.fieldName}
                autoComplete={ADDRESS_FIELDS.POSTAL_CODE.autoComplete}
                id={ADDRESS_FIELDS.POSTAL_CODE.fieldName}
                data-test-id={testId('zipCode')}
                value={mailingAddress.postalCode || ''}
                placeholder="12345"
                maxLength={ADDRESS_FIELDS.POSTAL_CODE.maxLength}
                onChange={this.handleUpdateMailingAddressOrName}
                required/>
              <div>{errors[ADDRESS_FIELDS.POSTAL_CODE.fieldName]}</div>
            </div>
            <div className={cn(css.formField, { [css.fieldError]: errors[ADDRESS_FIELDS.PHONE_NUMBER.fieldName] || formError })}>
              <label htmlFor={ADDRESS_FIELDS.PHONE_NUMBER.fieldName}>Phone</label>
              <input
                pattern=".*[0-9]+.*"
                name={ADDRESS_FIELDS.PHONE_NUMBER.fieldName}
                autoComplete={ADDRESS_FIELDS.PHONE_NUMBER.autoComplete}
                id={ADDRESS_FIELDS.PHONE_NUMBER.fieldName}
                data-test-id={testId('primaryPhoneNumber')}
                value={phone.voice.primary.number || ''}
                placeholder="123-456-7890"
                maxLength={ADDRESS_FIELDS.PHONE_NUMBER.maxLength}
                onChange={this.handleUpdateShippingPhone}
                required/>
              <div>{errors[ADDRESS_FIELDS.PHONE_NUMBER.fieldName]}</div>
            </div>
          </div>
          {
            !isReducedInfo && <div className={css.defaultAddress}>
              {isDefaultShippingAddress ? 'This is your primary address.' : (
                <div>
                  <input
                    type="checkbox"
                    name="defaultAddress"
                    id="defaultAddress"
                    data-test-id={testId('defaultAddressCheckBox')}
                    defaultChecked={isPrimary}
                    onChange={this.handleUpdateShippingDefaultAddress}/>
                  <label htmlFor="defaultAddress" data-test-id={testId('defaultAddressCheckBoxLabel')}>Make this my primary shipping address</label>
                </div>
              )}
            </div>
          }
          <div className={css.actions}>
            <button
              type="button" // This needs to be here as buttons inside of a form are automatically set to submit (but we can only have 1 submit button)
              className={css.cancel}
              onClick={this.handleCancelClick}
              disabled={isLoading}
              data-test-id={testId('cancelButton')}>Cancel</button>
            <button
              type="submit"
              className={css.save}
              disabled={!isFormValid || isLoading}
              data-test-id={testId('saveAddress')}>
              {isLoading ? 'Saving…' : primaryVerbiage || 'Save'}
            </button>
          </div>
        </form>
        { !isReducedInfo && <ShippingDisclaimer/> }
      </div>
    );
  }
}

ShippingForm.contextTypes = {
  testId: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired
};

ShippingForm.propTypes = {
  shipping: PropTypes.object.isRequired,
  returnTo: PropTypes.string,
  onChangeShippingAddress: PropTypes.func.isRequired,
  onUpdateMailingAddressOrName: PropTypes.func.isRequired,
  onUpdateShippingPhone: PropTypes.func.isRequired,
  onResetShippingForm: PropTypes.func.isRequired
};

export default ShippingForm;
